import React, { useEffect, useState, useCallback } from "react";
import TopNav from "../../Components/Dashboards/TopNav";
import Notifications from "../../Components/Dashboards/Notifications";
import NavHeader2 from "../../Components/Dashboards/NavHeader2";
import Sidebar from "../../Components/Dashboards/Sidebar";
import Footer from "../../Components/Dashboards/Footer";
import BreadcrumTop from "../../Components/Dashboards/BreadcrumTop";
import Nulls from "../../Components/Dashboards/Nulls";
import Spinner from "../../Spinner/Spinner";
import DataTable from "react-data-table-component";
import "../../Components/Dashboards/../../libs/css/dataTable.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { API_ROUTES } from "../../utils/constants";
import { getToken } from "../../utils/getToken";
import { apiFetch } from "../../utils/apiFetch";

const GAMES_API = API_ROUTES.GAMES_API;

export default function ManageGames(props) {
  const { isLoaded } = props;
  const navigate = useNavigate();

  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterMarketer, setFilterMarketer] = useState("");
  const [filterChannel, setFilterChannel] = useState("");

  // Derived unique values for filter dropdowns
  const [filterChannels, setFilterChannels] = useState([]);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterMarketers, setFilterMarketers] = useState([]);

  // Dropdown data
  const [companies, setCompanies] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [products, setProducts] = useState([]);
  const [channels, setChannels] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Form fields
  const [formChannel, setFormChannel] = useState("SecureD");
  const [formCompanyId, setFormCompanyId] = useState("");
  const [formCompanyName, setFormCompanyName] = useState("");
  const [formMarketerId, setFormMarketerId] = useState("");
  const [formMarketerName, setFormMarketerName] = useState("");
  const [formProductId, setFormProductId] = useState("");
  const [formGameName, setFormGameName] = useState("");
  const [formPId, setFormPId] = useState("");
  const [formRedirectUrl, setFormRedirectUrl] = useState("");
  const [formMsisdnBase, setFormMsisdnBase] = useState("http://sales.yangagames.net");
  const [formExtraParams, setFormExtraParams] = useState("");
  const [formActive, setFormActive] = useState(true);

  // Generate folder modal state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateGameId, setGenerateGameId] = useState(null);
  const [generateGameName, setGenerateGameName] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    document.title = "Partner Games | Dashboard";
  }, []);

  const getAuthHeaders = () => {
    const token = getToken();
    if (!token) {
      Swal.fire({ title: "Session Expired", text: "Please log in again.", icon: "error" }).then(() => {
        localStorage.removeItem("userVASData");
        localStorage.removeItem("companyName");
        navigate("/login");
      });
      return null;
    }
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  };

  // ── Fetch games list ────────────────────────────────────────────────────────
  const fetchGames = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiFetch(GAMES_API, { method: "GET" });
      const json = await response.json();
      const data = json.data || [];
      setGames(data);
      setFilterChannels([...new Set(data.map((g) => g.channel).filter(Boolean))].sort());
      setFilterCompanies([...new Set(data.map((g) => g.company_name).filter(Boolean))].sort());
      setFilterMarketers([...new Set(data.map((g) => g.marketer_name).filter(Boolean))].sort());
    } catch (error) {
      Swal.fire({ title: "Error", text: "Could not load games.", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // ── Fetch dropdown data (same pattern as /filter/data) ─────────────────────
  const fetchCompanies = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(API_ROUTES.GET_ALL_COMPANY, { method: "GET", headers });
      const json = await res.json();
      setCompanies(Array.isArray(json) ? json : []);
    } catch {
      setCompanies([]);
    }
  }, []);

  const fetchMarketers = useCallback(async (companyId) => {
    if (!companyId) { setMarketers([]); setProducts([]); return; }
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(`${API_ROUTES.GET_ALL_COMPANY_MARKETER}/${encodeURIComponent(companyId)}`, { method: "GET", headers });
      const json = await res.json();
      setMarketers(Array.isArray(json) ? json : []);
      setProducts([]);
    } catch {
      setMarketers([]);
    }
  }, []);

  const fetchProducts = useCallback(async (marketerId, companyId) => {
    if (!marketerId) { setProducts([]); return; }
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(API_ROUTES.GET_GAMES, {
        method: "POST",
        headers,
        body: JSON.stringify({ CompanyId: companyId, MarketerId: marketerId }),
      });
      const json = await res.json();
      setProducts(Array.isArray(json) ? json : []);
    } catch {
      setProducts([]);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(API_ROUTES.GET_ALL_CHANNELS, { method: "GET", headers });
      const json = await res.json();
      setChannels(json.data || []);
    } catch {
      setChannels([]);
    }
  }, []);

  // ── Modal open/close ────────────────────────────────────────────────────────
  const openModal = () => {
    setFormChannel("SecureD");
    setFormCompanyId("");
    setFormCompanyName("");
    setFormMarketerId("");
    setFormMarketerName("");
    setFormProductId("");
    setFormGameName("");
    setFormPId("");
    setFormRedirectUrl("");
    setFormMsisdnBase("http://sales.yangagames.net");
    setFormExtraParams("");
    setFormActive(true);
    setFormErrors({});
    fetchCompanies();
    fetchChannels();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormErrors({});
  };

  // ── Cascade: company → marketer → product ──────────────────────────────────
  const handleCompanyChange = (e) => {
    const selected = companies.find((c) => String(c.id) === e.target.value);
    setFormCompanyId(e.target.value);
    setFormCompanyName(selected?.companyName || "");
    setFormMarketerId("");
    setFormMarketerName("");
    setFormPId("");
    setFormRedirectUrl("");
    setFormProductId("");
    fetchMarketers(e.target.value);
  };

  const handleMarketerChange = (e) => {
    const selected = marketers.find((m) => String(m.id) === e.target.value);
    setFormMarketerId(e.target.value);
    setFormMarketerName(selected?.marketerName || selected?.name || "");
    setFormPId(selected?.pId || "");
    setFormProductId("");
    fetchProducts(e.target.value, formCompanyId);
  };

  const toCamelCase = (str) =>
    str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .split(" ")
      .filter(Boolean)
      .map((word, i) =>
        i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");

  const handleProductChange = (e) => {
    const selected = products.find((p) => String(p.id) === e.target.value);
    setFormProductId(selected?.productId || "");
    if (selected?.name) setFormGameName(toCamelCase(selected.name));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!formCompanyId) errors.company_id = "Company is required.";
    if (!formMarketerName.trim()) errors.marketer_name = "Marketer is required.";
    if (!formProductId.trim()) errors.product_id = "Product is required.";
    if (!formGameName.trim()) errors.game_name = "Game name is required.";
    if (formExtraParams.trim()) {
      try { JSON.parse(formExtraParams); } catch {
        errors.include_extra_params = "Must be valid JSON e.g. {\"key\": \"value\"}";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Submit: add game ────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const body = {
        channel: formChannel || "SecureD",
        company_name: formCompanyName || null,
        marketer_name: formMarketerName,
        game_name: formGameName.trim(),
        pId: formPId.trim() || null,
        product_id: formProductId.trim() || null,
        redirect_url: formRedirectUrl.trim() || null,
        msisdn_redirect_base: formMsisdnBase.trim() || "http://sales.yangagames.net",
        include_extra_params: formExtraParams.trim() || null,
        active: formActive ? 1 : 0,
      };

      const res = await apiFetch(GAMES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Failed to add game.");

      Swal.fire({ title: "Success", text: "Game added successfully.", icon: "success", timer: 2000, showConfirmButton: false });
      closeModal();
      fetchGames();
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message || "Something went wrong.", icon: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Generate folder modal ───────────────────────────────────────────────────
  const openGenerateModal = (game) => {
    setGenerateGameId(game.id);
    setGenerateGameName(game.game_name);
    setBannerFile(null);
    setShowGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setBannerFile(null);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!bannerFile) {
      Swal.fire({ title: "Image Required", text: "Please select a banner image before generating the folder.", icon: "warning" });
      return;
    }
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append("id", generateGameId);
      formData.append("banner_image", bannerFile);

      const res = await apiFetch(`${GAMES_API}?action=generate`, {
        method: "POST",
        body: formData,
      });

      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Generation failed.");

      if (!json.data?.folder_created) {
        Swal.fire({ title: "Folder Already Exists", text: "This game folder has already been generated.", icon: "error" });
        closeGenerateModal();
        return;
      }

      Swal.fire({ title: "Done", text: "Folder created successfully.", icon: "success", timer: 2000, showConfirmButton: false });
      closeGenerateModal();
      fetchGames();
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message || "Something went wrong.", icon: "error" });
    } finally {
      setGenerating(false);
    }
  };

  // ── Table columns ───────────────────────────────────────────────────────────
  const columns = [
    { name: "Channel", selector: (r) => r.channel, sortable: true, width: "100px" },
    { name: "Marketer", selector: (r) => r.marketer_name, sortable: true },
    { name: "Company", selector: (r) => r.company_name || "—", sortable: true },
    { name: "Game", selector: (r) => r.game_name, sortable: true },
    { name: "Partner ID", selector: (r) => r.pId || "—", sortable: true },
    { name: "Product ID", selector: (r) => r.product_id || "—", sortable: true },
    {
      name: "Status",
      selector: (r) => r.active,
      width: "90px",
      cell: (r) => (
        <span className={`badge ${r.active == 1 ? "bg-success" : "bg-secondary"}`}>
          {r.active == 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <div className="text-left th_data mt-3 pb-4 px-3">
      <div className="row mb-2">
        <div className="col-md-4"><strong>Trfsrc:</strong> {data.trfsrc || "—"}</div>
      </div>
      <div className="row mb-2">
        <div className="col-md-12"><strong>Offer URL:</strong> {data.offer_url ? <><span style={{ textTransform: "lowercase" }}>{data.msisdn_redirect_base || ""}</span>{data.offer_url}</> : "—"}</div>
      </div>
      <div className="row mb-2">
        <div className="col-md-6"><strong>Redirect URL:</strong><span style={{ textTransform: "lowercase" }}> {data.redirect_url || "—"}</span></div>
        {/* <div className="col-md-6"><strong>MSISDN Base:</strong> {data.msisdn_redirect_base || "—"}</div> */}
      </div>
      <div className="row mb-2">
        {/* <div className="col-md-6"><strong>Banner Image:</strong> {data.banner_image || "—"}</div> */}
        <div className="col-md-6"><strong>Extra Params:</strong> {data.include_extra_params || "—"}</div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6"><strong>Created:</strong> {data.created_at ? new Date(data.created_at).toLocaleString("en-GB") : "—"}</div>
        <div className="col-md-6"><strong>Updated:</strong> {data.updated_at ? new Date(data.updated_at).toLocaleString("en-GB") : "—"}</div>
      </div>
      {data.active == 1 && (
        <div>
          <button
            style={{ fontSize: "13px", backgroundColor: "#6a73fa", border: "none", color: "#fff", padding: "8px 14px", borderRadius: "6px" }}
            onClick={() => openGenerateModal(data)}
          >
            Generate Folder
          </button>
        </div>
      )}
    </div>
  );

  const filteredData = games.filter(
    (g) =>
      (!filterCompany || g.company_name === filterCompany) &&
      (!filterMarketer || g.marketer_name === filterMarketer) &&
      (!filterChannel || g.channel === filterChannel)
  );

  return (
    <>
      <Nulls />
      <div id="main-wrapper" className={isLoaded ? "show" : ""}>
        <TopNav />
        <Notifications />
        <NavHeader2 />
        <Sidebar />
        <div className="content-body mt--10">
          <div className="container-fluid pl-xs-0 pr-xs-0 pt-xs-0">
            <BreadcrumTop title="Partner Games" />

            <div className="row">
              <div className="container d-flex justify-content-end align-items-center mb-4">
                <div className="col-md-9 col-sm-12 mx-1 d-flex justify-content-end gap-2 align-items-center flex-wrap">
                  <select
                    className="form-select form-control2"
                    style={{ maxWidth: "150px" }}
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                  >
                    <option value="">All Channels</option>
                    {filterChannels.map((ch) => (
                      <option key={ch} value={ch}>{ch}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-control2"
                    style={{ maxWidth: "180px" }}
                    value={filterCompany}
                    onChange={(e) => setFilterCompany(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {filterCompanies.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-control2"
                    style={{ maxWidth: "180px" }}
                    value={filterMarketer}
                    onChange={(e) => setFilterMarketer(e.target.value)}
                  >
                    <option value="">All Marketers</option>
                    {filterMarketers.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <button className="btn btn-primary text-nowrap" onClick={openModal}>
                    Add Game
                  </button>
                </div>
              </div>
            </div>

            <div className="row">
              {isLoading ? (
                <Spinner loading={isLoading} />
              ) : (
                <DataTable
                  className="services_datatable display text-nowrap"
                  expandableRows
                  expandableRowsComponent={ExpandedComponent}
                  columns={columns}
                  data={filteredData}
                  highlightOnHover
                  pagination
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 50]}
                  noDataComponent={
                    <div className="py-4 text-muted">
                      No games found. Click <strong>Add Game</strong> to create one.
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ── Add Game Modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Game</h5>
                <button type="button" className="btn-close" onClick={closeModal} disabled={submitting}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">

                    {/* Channel */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Channel</label>
                      <select
                        className="form-select py-3_ form-control2"
                        value={formChannel}
                        onChange={(e) => setFormChannel(e.target.value)}
                        disabled={submitting}
                      >
                        {channels.length > 0 ? (
                          channels.map((ch) => (
                            <option key={ch.id} value={ch.value}>{ch.name}</option>
                          ))
                        ) : (
                          <>
                            <option value="SecureD">SecureD</option>
                            <option value="MFilter">MFilter</option>
                            <option value="Evina">Evina</option>
                          </>
                        )}
                      </select>
                    </div>

                    {/* Company */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Company <span className="text-danger">*</span></label>
                      <select
                        className={`form-select py-3_ form-control2 ${formErrors.company_id ? "is-invalid" : ""}`}
                        value={formCompanyId}
                        onChange={handleCompanyChange}
                        disabled={submitting}
                      >
                        <option value="">Select Company</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.companyName}</option>
                        ))}
                      </select>
                      {formErrors.company_id && <div className="invalid-feedback">{formErrors.company_id}</div>}
                    </div>

                    {/* Marketer */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">
                        Marketer <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select py-3_ form-control2 ${formErrors.marketer_name ? "is-invalid" : ""}`}
                        value={formMarketerId}
                        onChange={handleMarketerChange}
                        disabled={submitting || !formCompanyId}
                      >
                        <option value="">
                          {formCompanyId ? "Select Marketer" : "Select a company first"}
                        </option>
                        {marketers.map((m) => (
                          <option key={m.id} value={m.id}>{m.marketerName || m.name}</option>
                        ))}
                      </select>
                      {formErrors.marketer_name && (
                        <div className="invalid-feedback">{formErrors.marketer_name}</div>
                      )}
                    </div>

                    {/* Product (from products dropdown) */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Product <span className="text-danger">*</span></label>
                      <select
                        className={`form-select py-3_ form-control2 ${formErrors.product_id ? "is-invalid" : ""}`}
                        value={products.find((p) => p.productId === formProductId)?.id || ""}
                        onChange={handleProductChange}
                        disabled={submitting || !formMarketerId}
                      >
                        <option value="">
                          {formMarketerId ? "Select Product" : "Select a marketer first"}
                        </option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      {formErrors.product_id && <div className="invalid-feedback">{formErrors.product_id}</div>}
                    </div>

                    {/* Game Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">
                        Game Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.game_name ? "is-invalid" : ""}`}
                        placeholder="e.g. arena"
                        value={formGameName}
                        onChange={(e) => setFormGameName(e.target.value)}
                        disabled={submitting}
                      />
                      {formErrors.game_name && <div className="invalid-feedback">{formErrors.game_name}</div>}
                    </div>

                    {/* Partner ID */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Partner ID (pId)</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Auto-filled from marketer"
                        value={formPId}
                        readOnly
                      />
                    </div>

                    {/* Redirect URL */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-semibold">Redirect URL</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter redirect URL"
                        value={formRedirectUrl}
                        onChange={(e) => setFormRedirectUrl(e.target.value)}
                      />
                    </div>

                    {/* MSISDN Redirect Base — hidden */}
                    <input type="hidden" value={formMsisdnBase} />

                    {/* Extra Params */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-semibold">Optional Extra Params (JSON)</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.include_extra_params ? "is-invalid" : ""}`}
                        placeholder='e.g. {"origin_banner": 2}'
                        value={formExtraParams}
                        onChange={(e) => setFormExtraParams(e.target.value)}
                        disabled={submitting}
                      />
                      {formErrors.include_extra_params && (
                        <div className="invalid-feedback">{formErrors.include_extra_params}</div>
                      )}
                    </div>

                    {/* Active — always true, hidden */}
                    <div style={{ display: "none" }}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="activeCheck"
                          checked={formActive}
                          onChange={(e) => setFormActive(e.target.checked)}
                        />
                      </div>
                    </div>

                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : "Add Game"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Generate Folder Modal ──────────────────────────────────────────── */}
      {showGenerateModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Generate Folder — {generateGameName}</h5>
                <button type="button" className="btn-close" onClick={closeGenerateModal} disabled={generating}></button>
              </div>
              <form onSubmit={handleGenerate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Banner Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      required
                      onChange={(e) => setBannerFile(e.target.files[0] || null)}
                      disabled={generating}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeGenerateModal} disabled={generating}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={generating}>
                    {generating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : "Generate Folder"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
